import { useState, useEffect } from 'react';
import { Connection, 
  Transaction,
  LAMPORTS_PER_SOL,
  PublicKey,
  SystemProgram } from '@solana/web3.js';
import { useWallet } from '@solana/wallet-adapter-react';
import { useTransactionPopup } from '../TransactionPopup';
import { QGEN_RECIPIENT, getSolanaRpcEndpoints } from '../../lib/solana';
import { saveSolanaPurchase, saveReferral } from '../../services/supabase';
import { useReferral } from '../../hooks/useReferral';
import { ethers } from 'ethers';

// Interface
interface SolPaymentProps {
  amount: string;
  userAddress?: string;
  referralAddress?: string;
  stake: boolean;
  onError: (error: string) => void;
  onSuccess: (hash: string) => void;
  onProcessing: (processing: boolean) => void;
}

// CORS proxy for fallback
const CORS_PROXY = 'https://corsproxy.io/?';

// Token price in USD
const TOKEN_PRICE_USD = 0.002;

// Chainlink SOL/USD price feed on Ethereum mainnet
const PRICE_FEEDS = {
  solana: '0x4ffC43a60e009B551865A93d232E33Fce9f01507'
};

// Chainlink Data Feed ABI (simplified for our needs)
const priceFeedAbi = [
  {
    inputs: [],
    name: 'latestRoundData',
    outputs: [
      { name: 'roundId', type: 'uint80' },
      { name: 'answer', type: 'int256' },
      { name: 'startedAt', type: 'uint256' },
      { name: 'updatedAt', type: 'uint256' },
      { name: 'answeredInRound', type: 'uint80' }
    ],
    stateMutability: 'view',
    type: 'function'
  }
];

export function SolPayment({
  amount,
  userAddress,
  referralAddress,
  stake,
  onError,
  onSuccess,
  onProcessing,
}: SolPaymentProps) {
  const { publicKey, sendTransaction } = useWallet();
  const { showTransaction } = useTransactionPopup();
  const [isProcessing, setIsProcessing] = useState(false);
  const { referrerAddress } = useReferral();
  const [success, setSuccess] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [loading, setLoading] = useState(true);

  const handlePayment = async () => {
    if (!publicKey) {
      onError('Please connect your wallet');
      return;
    }

    // Note: On Solana, referrals don't work, so we don't check the referral address format

    const inputAmount = parseFloat(amount);
    if (!amount || isNaN(inputAmount) || inputAmount <= 0) {
      onError('Please enter a valid amount');
      return;
    }

    try {
      setIsProcessing(true);
      onProcessing(true);

      // Try different RPC endpoints if one fails
      let connection: Connection | null = null;
      let blockhash: string | null = null;
      let lastError: Error | null = null;
      let connectedEndpoint: string | null = null;

      // Get the fallback RPC endpoints
      const rpcEndpoints = getSolanaRpcEndpoints();
      
      // First try the primary Helius endpoint
      try {
        const heliusEndpoint = rpcEndpoints[0]; // The first endpoint should be Helius
        
        connection = new Connection(heliusEndpoint, {
          commitment: 'confirmed',
          confirmTransactionInitialTimeout: 60000
        });
        
        // Test the connection with a lightweight call first
        const version = await connection.getVersion();
        
        // If that worked, get the blockhash
        const { blockhash: recentBlockhash } = await connection.getRecentBlockhash('finalized');
        blockhash = recentBlockhash;
        connectedEndpoint = heliusEndpoint;
      } catch (heliusError) {
        lastError = heliusError as Error;
        
        // If Helius fails, try other endpoints as fallbacks
        // Start from index 1 since we already tried the first endpoint
        for (let i = 1; i < rpcEndpoints.length; i++) {
          const endpoint = rpcEndpoints[i];
          try {
            connection = new Connection(endpoint, {
              commitment: 'confirmed',
              confirmTransactionInitialTimeout: 60000
            });
            
            // Test the connection
            await connection.getVersion();
            
            // Get blockhash
            const { blockhash: recentBlockhash } = await connection.getRecentBlockhash('finalized');
            blockhash = recentBlockhash;
            connectedEndpoint = endpoint;
            break;
          } catch (err) {
            // Continue to the next endpoint
          }
        }
      }
      
      // If direct connections failed, try with CORS proxy
      if (!connection || !blockhash) {
        for (const endpoint of rpcEndpoints) {
          try {
            const proxiedEndpoint = `${CORS_PROXY}${encodeURIComponent(endpoint)}`;
            
            connection = new Connection(proxiedEndpoint, {
              commitment: 'confirmed',
              confirmTransactionInitialTimeout: 60000
            });
            
            // Test the connection with a lightweight call first
            await connection.getVersion();
            
            // If that worked, get the blockhash
            const { blockhash: recentBlockhash } = await connection.getRecentBlockhash('finalized');
            blockhash = recentBlockhash;
            connectedEndpoint = proxiedEndpoint;
            break; // We got a valid connection and blockhash
          } catch (err) {
            lastError = err as Error;
            // Continue to the next endpoint
          }
        }
      }

      // If all endpoints failed
      if (!connection || !blockhash) {
        const errorMsg = `Failed to connect to any Solana RPC endpoint. Last error: ${lastError?.message || 'Unknown error'}`;
        throw new Error(errorMsg);
      }
      
      // Convert SOL to lamports
      const lamports = inputAmount * LAMPORTS_PER_SOL;
      
      // Create transaction
      const transaction = new Transaction();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;
      
      // Add instruction to send SOL
      transaction.add(
        SystemProgram.transfer({
          fromPubkey: publicKey,
          toPubkey: new PublicKey(QGEN_RECIPIENT),
          lamports: Math.floor(lamports)
        })
      );
      
      // If staking, add a memo instruction to indicate staking
      if (stake) {
        // Add memo instruction here if needed
      }
      
      const signature = await sendTransaction(transaction, connection);
      
      // Show pending transaction popup
      showTransaction('pending', `Purchasing $QGEN with ${amount} SOL...`, 'sol', signature);
      
      // Wait for confirmation with retry mechanism
      let confirmationAttempts = 0;
      const maxConfirmationAttempts = 3;
      let confirmed = false;
      
      while (confirmationAttempts < maxConfirmationAttempts && !confirmed) {
        try {
          confirmationAttempts++;
          const confirmation = await connection.confirmTransaction(signature, 'confirmed');
          
          if (confirmation.value.err) {
            if (confirmationAttempts >= maxConfirmationAttempts) {
              showTransaction('error', 'Transaction failed during confirmation.', 'sol', signature);
              throw new Error(`Transaction failed during confirmation: ${JSON.stringify(confirmation.value.err)}`);
            }
          } else {
            confirmed = true;
          }
        } catch (err) {
          if (confirmationAttempts >= maxConfirmationAttempts) {
            showTransaction('error', 'Failed to confirm transaction after multiple attempts.', 'sol', signature);
            throw new Error('Failed to confirm transaction after multiple attempts.');
          }
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
      // Calculate token amount
      const amountUsd = inputAmount * await fetchSolPrice();
      const tokenAmount = amountUsd / TOKEN_PRICE_USD;
      
      try {
        // Save purchase to Supabase
        const savedPurchase = await saveSolanaPurchase({
          user_address: publicKey.toBase58(),
          referrer_address: referrerAddress || undefined,
          amount_usd: amountUsd,
          token_amount: tokenAmount,
          token_type: 'SOL',
          is_staked: stake,
          transaction_hash: signature
        });
        
        // If there's a referrer, save the referral
        if (referrerAddress) {
          await saveReferral({
            referrer_address: referrerAddress,
            referee_address: publicKey.toBase58(),
            amount: amountUsd,
            reward: tokenAmount * 0.05 // 5% reward
          });
        }
      } catch (dbError) {
        // Continue even if DB save fails - user already completed transaction
      }
      
      // Update UI
      setSuccess(true);
      setTxHash(signature);
      showTransaction('success', `Successfully purchased ${tokenAmount.toFixed(2)} $QGEN tokens!`, 'sol', signature);
      onSuccess(signature);
    } catch (error: any) {
      let errorMessage = 'Transaction failed.';
      
      if (error.message && typeof error.message === 'string') {
        if (error.message.includes('User rejected') || error.message.includes('rejected the request')) {
          errorMessage = 'Transaction cancelled by user.';
          console.log('Transaction was rejected by the user. This is not an error with the app.');
        } else if (error.message.includes('insufficient funds')) {
          errorMessage = 'Insufficient funds in your wallet.';
        } else if (error.name === 'WalletSendTransactionError') {
          // Handle specific wallet adapter errors
          errorMessage = 'Wallet transaction was rejected. Please try again.';
        } else {
          errorMessage = `Error: ${error.message.slice(0, 120)}`;
          console.error('Transaction error:', error);
        }
      }
      
      onError(errorMessage);
      showTransaction('error', errorMessage, 'sol');
    } finally {
      setIsProcessing(false);
      onProcessing(false);
    }
  };
  
  // Helper function to get SOL price from Chainlink oracle
  const fetchSolPrice = async (): Promise<number> => {
    try {
      // Use Ethereum mainnet to access the Chainlink oracle
      const provider = new ethers.JsonRpcProvider('https://eth.llamarpc.com');
      const chainlinkOracle = new ethers.Contract(
        PRICE_FEEDS.solana,
        priceFeedAbi,
        provider
      );
      
      const roundData = await chainlinkOracle.latestRoundData();
      const price = Number(ethers.formatUnits(roundData.answer, 8));
      
      return price;
    } catch (error) {
      // Attempt fallback to CoinGecko if Chainlink fails
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
        const data = await response.json();
        const price = data.solana?.usd;
        if (price) {
          return price;
        }
      } catch (fallbackError) {
        // Silence fallback error
      }
      
      // Default fallback price if all sources fail
      return 100;
    }
  };
  
  // Handle success
  useEffect(() => {
    let cleanup = false;
    
    if (success && txHash && !cleanup) {
      // Use flag to prevent duplicate calls during effect cleanup
      cleanup = true;
      // Use setTimeout to break out of React's execution stack
      setTimeout(() => {
        onSuccess(txHash);
        setSuccess(false); // Reset success state
      }, 0);
    }
    
    // Return cleanup function
    return () => { cleanup = true; };
  }, [success, txHash]);
  
  return { handlePayment };
}